import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { useT } from '@/i18n/LanguageContext';

// Google client ID — loaded from env var in production, buttons always visible
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token?: string; error?: string }) => void;
          }) => { requestAccessToken: () => void };
        };
      };
    };
  }
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function WechatIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
      <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05a6.597 6.597 0 0 1-.271-1.879c0-3.726 3.395-6.758 7.6-6.758.34 0 .672.024 1 .063C17.086 4.586 13.274 2.188 8.69 2.188zm-2.6 4.408c.56 0 1.013.452 1.013 1.01 0 .56-.453 1.013-1.013 1.013-.56 0-1.012-.453-1.012-1.013 0-.558.452-1.01 1.012-1.01zm5.108 0c.56 0 1.012.452 1.012 1.01 0 .56-.452 1.013-1.012 1.013-.56 0-1.013-.453-1.013-1.013 0-.558.453-1.01 1.013-1.01zM16.83 9.184c-3.643 0-6.6 2.625-6.6 5.86 0 3.236 2.957 5.862 6.6 5.862.69 0 1.37-.086 2.02-.268a.68.68 0 0 1 .567.078l1.482.868a.258.258 0 0 0 .13.042c.127 0 .23-.103.23-.23a.35.35 0 0 0-.037-.167l-.303-1.152a.466.466 0 0 1 .168-.524C22.614 18.665 23.43 17 23.43 15.044c0-3.235-2.957-5.86-6.6-5.86zm-2.395 3.504c.437 0 .792.354.792.79 0 .438-.355.793-.792.793a.793.793 0 0 1-.792-.792c0-.437.355-.791.792-.791zm4.79 0c.438 0 .793.354.793.79 0 .438-.355.793-.792.793a.793.793 0 0 1-.792-.792c0-.437.355-.791.792-.791z"/>
    </svg>
  );
}

interface SsoButtonsProps {
  onError?: (error: string) => void;
}

export function SsoButtons({ onError }: SsoButtonsProps) {
  const navigate = useNavigate();
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);
  const t = useT();
  const [loading, setLoading] = useState<'google' | 'wechat' | null>(null);
  const [gsiReady, setGsiReady] = useState(false);

  // Load Google Identity Services script
  useEffect(() => {
    if (window.google?.accounts?.oauth2) {
      setGsiReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => setGsiReady(true);
    document.head.appendChild(script);
  }, []);

  const handleGoogleClick = useCallback(() => {
    if (!GOOGLE_CLIENT_ID) {
      onError?.('Google SSO is not yet configured.');
      return;
    }
    if (!window.google?.accounts?.oauth2) {
      onError?.('Google sign-in is loading. Please try again.');
      return;
    }
    setLoading('google');

    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'openid email profile',
      callback: async (response) => {
        if (response.error || !response.access_token) {
          setLoading(null);
          onError?.(t.sso?.error || 'SSO sign-in failed. Please try again.');
          return;
        }
        try {
          const result = await loginWithGoogle(response.access_token);
          if (result === true) {
            navigate('/', { replace: true });
          } else {
            onError?.(result);
          }
        } catch {
          onError?.(t.sso?.error || 'SSO sign-in failed. Please try again.');
        } finally {
          setLoading(null);
        }
      },
    });
    client.requestAccessToken();
  }, [gsiReady, loginWithGoogle, navigate, onError, t]);

  const handleWechatClick = async () => {
    setLoading('wechat');
    try {
      const res = await fetch('/api/auth/wechat');
      if (!res.ok) {
        if (res.status === 501) {
          onError?.('WeChat SSO is not yet configured.');
          return;
        }
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to start WeChat login');
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch {
      onError?.(t.sso?.error || 'SSO sign-in failed. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-3">
      {/* Google SSO — always visible, disabled if GSI script not loaded */}
      <button
        type="button"
        onClick={handleGoogleClick}
        disabled={loading === 'google' || !gsiReady}
        className="w-full flex items-center justify-center gap-3 py-3.5 px-6 bg-white border border-[#DDDDDD] rounded-full
          text-[#3F3F3F] font-medium text-sm
          hover:border-[#BBBBBB] hover:bg-[#F7F7F7] transition-all duration-200
          disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading === 'google' ? (
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <>
            <GoogleIcon />
            {t.sso?.googleSignIn || 'Sign in with Google'}
          </>
        )}
      </button>

      {/* WeChat SSO — hidden until WeChat Open Platform credentials are configured */}
      {/* TODO: Uncomment when WECHAT_APP_ID is set in production
      <button
        type="button"
        onClick={handleWechatClick}
        disabled={loading === 'wechat'}
        className="w-full flex items-center justify-center gap-3 py-3.5 px-6 bg-[#07C160] rounded-full
          text-white font-medium text-sm
          hover:bg-[#06AD56] transition-all duration-200
          disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading === 'wechat' ? (
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <>
            <WechatIcon />
            {t.sso?.wechatSignIn || 'Sign in with WeChat'}
          </>
        )}
      </button>
      */}

      {/* Divider */}
      <div className="flex items-center gap-3 py-1">
        <div className="flex-1 h-px bg-[#DDDDDD]" />
        <span className="text-sm text-[#9A9A9A]">{t.common?.or || 'or'}</span>
        <div className="flex-1 h-px bg-[#DDDDDD]" />
      </div>
    </div>
  );
}
